/**
 * TextAreaEditor — interactive side-by-side editor for text_box positioning.
 *
 * Left panel: preview image with draggable text area overlay + alignment toolbar.
 * Right panel: form fields for all rendering metadata.
 *
 * Reused in both Pack-level defaults and Item-level dialog.
 */
import { useState, useCallback, useRef, useEffect } from 'react'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { ColorInput } from '@/components/ui/ColorInput'
import { FontColorInput } from '@/components/ui/FontColorInput'
import { contentPacksApi } from '@/lib/api/contentPacks'
import type { ItemRenderMetadata, ContentPackItem, ColorInfo, TextAreaInfo, TextBox } from '@/lib/api/contentPacks'
import { useTextAreaDrag, alignTextBox } from '../hooks/useTextAreaDrag'
import type { DragMode } from '../hooks/useTextAreaDrag'
import clsx from 'clsx'

const PREVIEW_PHRASES = [
  { label: '短', text: '吉星高照' },
  { label: '中', text: '幸运符文字预览' },
  { label: '长', text: '愿你在每一个清晨都能感受到生活的温暖与美好' },
  { label: '自定义', text: '' },
]
const CUSTOM_PHRASE_IDX = PREVIEW_PHRASES.length - 1

const MODE_LABELS: Record<string, string> = { auto: '自动', fixed: '固定', palette: '调色板' }

// ============================================================================
// Drag Handle
// ============================================================================

function DragHandle({
  position,
  onPointerDown,
}: {
  position: 'nw' | 'ne' | 'sw' | 'se' | 'n' | 's' | 'e' | 'w'
  onPointerDown: (e: React.PointerEvent) => void
}) {
  const isCorner = position.length === 2
  const cursors: Record<string, string> = {
    nw: 'nw-resize', ne: 'ne-resize', sw: 'sw-resize', se: 'se-resize',
    n: 'n-resize', s: 's-resize', e: 'e-resize', w: 'w-resize',
  }

  const posStyle: React.CSSProperties = {}
  if (position.includes('n')) posStyle.top = -4
  if (position.includes('s')) posStyle.bottom = -4
  if (position.includes('w')) posStyle.left = -4
  if (position.includes('e')) posStyle.right = -4
  if (position === 'n' || position === 's') { posStyle.left = '50%'; posStyle.transform = 'translateX(-50%)' }
  if (position === 'e' || position === 'w') { posStyle.top = '50%'; posStyle.transform = 'translateY(-50%)' }

  return (
    <div
      className="absolute bg-white border-2 border-blue-500 rounded-sm z-10"
      style={{
        width: isCorner ? 8 : (position === 'n' || position === 's' ? 16 : 8),
        height: isCorner ? 8 : (position === 'e' || position === 'w' ? 16 : 8),
        cursor: cursors[position],
        ...posStyle,
      }}
      onPointerDown={onPointerDown}
    />
  )
}

// ============================================================================
// Preview Panel (Left Side)
// ============================================================================

function PreviewPanel({
  packId,
  itemId,
  meta,
  items,
  localBox,
  mirrorMode,
  onMirrorToggle,
  onBoxChange,
  onBoxCommit,
}: {
  packId: string
  itemId?: string
  meta: ItemRenderMetadata
  items?: ContentPackItem[]
  localBox: TextBox
  mirrorMode: boolean
  onMirrorToggle: () => void
  onBoxChange: (box: TextBox) => void
  onBoxCommit: (box: TextBox) => void
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [colorInfo, setColorInfo] = useState<ColorInfo | null>(null)
  const [textAreaInfo, setTextAreaInfo] = useState<TextAreaInfo | null>(null)
  const [loading, setLoading] = useState(false)
  const [phraseIdx, setPhraseIdx] = useState(1)
  const [customPhrase, setCustomPhrase] = useState('')
  const [selectedBgId, setSelectedBgId] = useState<string | undefined>(itemId)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const prevKeyRef = useRef<string>('')
  const prevMetaRef = useRef<string>(JSON.stringify(meta))
  const imageRef = useRef<HTMLImageElement>(null)
  const previewUrlRef = useRef<string | null>(null)

  const bgItems = items?.filter(i => i.item_type === 'background') ?? []
  const vp = meta.view_port ?? { width: 1080, height: 1920 }

  const fetchPreview = useCallback(async (metadata: ItemRenderMetadata, phrase: string, bgId?: string) => {
    setLoading(true)
    try {
      const result = bgId
        ? await contentPacksApi.previewItem(packId, bgId, metadata, phrase)
        : await contentPacksApi.previewPack(packId, metadata, phrase)
      const url = URL.createObjectURL(result.blob)
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current)
      previewUrlRef.current = url
      setPreviewUrl(url)
      setColorInfo(result.colorInfo)
      setTextAreaInfo(result.textArea)
    } catch (err) { console.warn('[TextAreaEditor] preview failed:', err) }
    finally { setLoading(false) }
  }, [packId])

  const activePhrase = phraseIdx === CUSTOM_PHRASE_IDX ? customPhrase : PREVIEW_PHRASES[phraseIdx].text

  useEffect(() => {
    const key = JSON.stringify({ meta, phraseIdx, selectedBgId, customPhrase: phraseIdx === CUSTOM_PHRASE_IDX ? customPhrase : '' })
    if (key === prevKeyRef.current) return
    prevKeyRef.current = key
    if (phraseIdx === CUSTOM_PHRASE_IDX && !customPhrase) return
    const metaJson = JSON.stringify(meta)
    const metaChanged = metaJson !== prevMetaRef.current
    prevMetaRef.current = metaJson
    const delay = (metaChanged || phraseIdx === CUSTOM_PHRASE_IDX) ? 600 : 0
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(
      () => fetchPreview(meta, activePhrase, selectedBgId),
      delay,
    )
    return () => { if (timerRef.current) clearTimeout(timerRef.current) }
  }, [meta, phraseIdx, selectedBgId, customPhrase, activePhrase, fetchPreview])

  useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current)
  }, [])

  const boxPct = {
    left: (localBox.left / vp.width) * 100,
    top: (localBox.top / vp.height) * 100,
    width: (localBox.width / vp.width) * 100,
    height: (localBox.height / vp.height) * 100,
  }

  const getImageRect = () => imageRef.current?.getBoundingClientRect() ?? new DOMRect()

  const { isDragging, handlePointerDown, handlePointerMove, handlePointerUp } = useTextAreaDrag({
    viewPort: vp,
    textBox: localBox,
    mirrorMode,
    onUpdate: onBoxChange,
    onCommit: onBoxCommit,
  })

  const handleAlign = (action: 'h-center' | 'v-center' | 'top-third' | 'bot-third' | 'full-width') => {
    const newBox = alignTextBox(action, localBox, vp)
    onBoxChange(newBox)
    onBoxCommit(newBox)
  }

  const hasBox = localBox.width > 0 && localBox.height > 0

  return (
    <div className="flex flex-col bg-gray-900 rounded-l-lg overflow-hidden" style={{ flex: '1.2 1 0%' }}>
      {/* Top toolbar */}
      <div className="flex items-center gap-2 px-3 py-2 bg-white/5 border-b border-white/10 text-xs">
        <span className="text-white/50">Text:</span>
        <div className="flex gap-0.5">
          {PREVIEW_PHRASES.map((p, i) => (
            <button
              key={i}
              type="button"
              className={clsx(
                'px-2 py-0.5 rounded text-xs',
                i === phraseIdx ? 'bg-blue-600 text-white' : 'bg-white/10 text-white/60 hover:bg-white/20',
              )}
              onClick={() => setPhraseIdx(i)}
            >{p.label}</button>
          ))}
        </div>
        {phraseIdx === CUSTOM_PHRASE_IDX && (
          <input
            type="text"
            className="flex-1 min-w-0 text-xs bg-white/10 text-white border border-white/20 rounded px-2 py-0.5 placeholder-white/30 focus:outline-none focus:border-blue-500"
            placeholder="输入预览文字…"
            value={customPhrase}
            onChange={e => setCustomPhrase(e.target.value)}
          />
        )}
        {bgItems.length > 1 && (
          <>
            <span className="text-white/20 mx-1">|</span>
            <select
              className="text-xs bg-white/10 text-white/60 border border-white/20 rounded px-1 py-0.5 max-w-[100px]"
              value={selectedBgId ?? ''}
              onChange={e => setSelectedBgId(e.target.value || undefined)}
            >
              {!itemId && <option value="">随机</option>}
              {bgItems.map(bg => <option key={bg.id} value={bg.id}>{bg.filename.replace(/\.[^.]+$/, '')}</option>)}
            </select>
          </>
        )}
        <div className="flex-1" />
        <span className="text-white/30">{vp.width} x {vp.height}</span>
        {loading && <span className="text-white/40 animate-pulse">渲染中...</span>}
      </div>

      {/* Image + interactive overlay */}
      <div
        className="flex-1 flex items-center justify-center p-4 relative select-none"
        onPointerMove={e => isDragging && handlePointerMove(e, getImageRect())}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      >
        {previewUrl ? (
          <div className="relative" style={{ maxHeight: '100%' }}>
            <img
              ref={imageRef}
              src={previewUrl}
              alt="preview"
              className="max-h-full max-w-full object-contain rounded"
              style={{ maxHeight: 500 }}
              draggable={false}
            />
            {/* Draggable text area overlay */}
            {hasBox && (
              <div
                className="absolute border-2 border-blue-400/90 rounded-sm"
                style={{
                  left: `${boxPct.left}%`,
                  top: `${boxPct.top}%`,
                  width: `${boxPct.width}%`,
                  height: `${boxPct.height}%`,
                  backgroundColor: 'rgba(96, 165, 250, 0.08)',
                  cursor: isDragging ? 'grabbing' : 'grab',
                }}
                onPointerDown={e => handlePointerDown(e, 'move')}
              >
                <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] text-blue-300 bg-black/60 px-1.5 py-0.5 rounded whitespace-nowrap pointer-events-none">
                  {localBox.width} x {localBox.height}
                </span>
                {(['nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w'] as const).map(pos => (
                  <DragHandle
                    key={pos}
                    position={pos}
                    onPointerDown={e => handlePointerDown(e, `resize-${pos}` as DragMode)}
                  />
                ))}
              </div>
            )}
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded">
                <span className="text-xs text-white bg-black/50 px-2 py-0.5 rounded-full">渲染中...</span>
              </div>
            )}
          </div>
        ) : (
          <div className="text-white/30 text-sm">
            {loading ? '加载预览...' : '设置 view_port 和 text_box 后可见预览'}
          </div>
        )}
      </div>

      {/* Bottom alignment toolbar */}
      <div className="flex items-center justify-center gap-1 px-3 py-2 bg-white/5 border-t border-white/10">
        <div className="flex gap-0.5 bg-white/[0.08] rounded-md p-0.5">
          {([
            ['h-center', 'H-Center'],
            ['v-center', 'V-Center'],
            ['top-third', 'Top ⅓'],
            ['bot-third', 'Bot ⅓'],
            ['full-width', 'Full W'],
          ] as const).map(([action, label]) => (
            <button
              key={action}
              type="button"
              className="px-2 py-1 text-[10px] text-white/70 hover:bg-white/15 rounded transition-colors"
              onClick={() => handleAlign(action)}
            >{label}</button>
          ))}
        </div>
        <div className="w-px h-5 bg-white/15 mx-1" />
        <button
          type="button"
          className={clsx(
            'px-2 py-1 text-[10px] rounded transition-colors',
            mirrorMode ? 'bg-blue-600 text-white' : 'text-white/70 hover:bg-white/15',
          )}
          onClick={onMirrorToggle}
          title="Mirror: 拖拽时左右边距保持对称"
        >↔ Mirror</button>
      </div>

      {/* Color info bar */}
      {colorInfo && !loading && (
        <div className="flex items-center gap-2 px-3 py-1 bg-white/5 border-t border-white/10 text-[10px] text-white/40 flex-wrap">
          <span>颜色: <strong className="text-white/60">{MODE_LABELS[colorInfo.configMode] || colorInfo.configMode}</strong></span>
          {colorInfo.resolvedColor && (
            <>
              <span className="text-white/20">|</span>
              <span className="flex items-center gap-1">
                <span className="inline-block w-2.5 h-2.5 rounded border border-white/30" style={{ backgroundColor: colorInfo.resolvedColor }} />
                <strong className="text-white/60">{colorInfo.resolvedColor}</strong>
              </span>
            </>
          )}
          {textAreaInfo && textAreaInfo.imgW > 0 && (
            <>
              <span className="text-white/20">|</span>
              <span>图片: {textAreaInfo.imgW}x{textAreaInfo.imgH}</span>
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Form Panel (Right Side)
// ============================================================================

function FormPanel({
  meta,
  onChange,
}: {
  meta: ItemRenderMetadata
  onChange: (updated: ItemRenderMetadata) => void
}) {
  const updateField = <K extends keyof ItemRenderMetadata>(key: K, value: ItemRenderMetadata[K]) => {
    onChange({ ...meta, [key]: value })
  }

  const vp = meta.view_port ?? { width: 0, height: 0 }
  const tb = meta.text_box ?? { top: 0, left: 0, width: 0, height: 0 }

  return (
    <div className="flex flex-col overflow-y-auto bg-white border-l border-gray-200" style={{ flex: '0.8 1 0%' }}>
      {/* Canvas size */}
      <div className="p-4 border-b border-gray-100">
        <div className="text-xs font-semibold text-gray-800 mb-2">画布尺寸</div>
        <div className="grid grid-cols-2 gap-2">
          <Input label="宽度" type="number" value={vp.width || ''} onChange={e => {
            const v = e.target.value ? parseInt(e.target.value) : 0
            updateField('view_port', { width: v, height: vp.height })
          }} />
          <Input label="高度" type="number" value={vp.height || ''} onChange={e => {
            const v = e.target.value ? parseInt(e.target.value) : 0
            updateField('view_port', { width: vp.width, height: v })
          }} />
        </div>
      </div>

      {/* Text area coordinates */}
      <div className="p-4 border-b border-gray-100">
        <div className="text-xs font-semibold text-gray-800 mb-0.5">文字区域</div>
        <div className="text-[10px] text-gray-400 mb-2">在预览图上拖拽或手动输入</div>
        <div className="grid grid-cols-2 gap-2">
          <Input label="Top" type="number" value={tb.top} onChange={e => {
            updateField('text_box', { ...tb, top: parseInt(e.target.value) || 0 })
          }} />
          <Input label="Left" type="number" value={tb.left} onChange={e => {
            updateField('text_box', { ...tb, left: parseInt(e.target.value) || 0 })
          }} />
          <Input label="Width" type="number" value={tb.width} onChange={e => {
            updateField('text_box', { ...tb, width: parseInt(e.target.value) || 0 })
          }} />
          <Input label="Height" type="number" value={tb.height} onChange={e => {
            updateField('text_box', { ...tb, height: parseInt(e.target.value) || 0 })
          }} />
        </div>
      </div>

      {/* Font color */}
      <div className="p-4 border-b border-gray-100">
        <div className="text-xs font-semibold text-gray-800 mb-2">字体颜色</div>
        <FontColorInput
          value={meta.font_color}
          onChange={v => updateField('font_color', v as ItemRenderMetadata['font_color'])}
        />
      </div>

      {/* Font size & layout options */}
      <div className="p-4 border-b border-gray-100">
        <div className="text-xs font-semibold text-gray-800 mb-2">字号</div>
        <div className="inline-flex rounded-md border border-gray-300 overflow-hidden mb-2">
          {(['auto', 'manual'] as const).map(mode => (
            <button
              key={mode}
              type="button"
              className={clsx(
                'px-3 py-1 text-xs font-medium transition-colors border-r last:border-r-0 border-gray-300',
                (meta.font_size == null ? 'auto' : 'manual') === mode ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50',
              )}
              onClick={() => {
                if (mode === 'auto') {
                  updateField('font_size', undefined)
                } else {
                  updateField('font_size', 48)
                }
              }}
            >{{ auto: '自适应', manual: '手动指定' }[mode]}</button>
          ))}
        </div>
        {meta.font_size != null && (
          <div className="mb-2">
            <label className="block text-[10px] text-gray-500 mb-0.5">字号 (设计像素)</label>
            <Input type="number" step="1" min="8" max="500" value={meta.font_size} onChange={e => updateField('font_size', e.target.value ? parseInt(e.target.value) : undefined)} />
          </div>
        )}
        <div className="text-xs font-semibold text-gray-800 mb-1.5 mt-3">排版策略</div>
        <div className="space-y-1.5">
          {([
            { key: 'layout_vertical_stack' as const, label: '短语竖排', hint: '4字成语逐字竖排', autoOnly: true },
            { key: 'layout_balanced_wrap' as const, label: '平衡换行', hint: '两行宽度尽量均衡', autoOnly: false },
            { key: 'layout_reject_unbalanced' as const, label: '拒绝不平衡', hint: '孤字/末行过长时缩小字号', autoOnly: true },
          ] as const).map(({ key, label, hint, autoOnly }) => {
            const disabled = autoOnly && meta.font_size != null
            return (
              <label key={key} className={clsx('flex items-center gap-2 text-xs', disabled && 'opacity-40')}>
                <input
                  type="checkbox"
                  className="rounded border-gray-300"
                  checked={meta[key] !== false}
                  disabled={disabled}
                  onChange={e => updateField(key, e.target.checked ? undefined : false)}
                />
                <span>{label}</span>
                <span className="text-[10px] text-gray-400">{hint}</span>
              </label>
            )
          })}
        </div>
      </div>

      {/* Text effect */}
      <div className="p-4 border-b border-gray-100">
        <div className="text-xs font-semibold text-gray-800 mb-2">文字效果</div>
        <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
          {(['none', 'outline', 'shadow'] as const).map(effect => (
            <button
              key={effect}
              type="button"
              className={clsx(
                'px-3 py-1 text-xs font-medium transition-colors border-r last:border-r-0 border-gray-300',
                (meta.text_effect ?? 'none') === effect ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50',
              )}
              onClick={() => {
                const updated: ItemRenderMetadata = { ...meta, text_effect: effect === 'none' ? undefined : effect }
                if (effect === 'none' || effect === 'outline') {
                  updated.shadow_blur = undefined; updated.shadow_opacity = undefined
                  updated.shadow_color = undefined; updated.shadow_distance = undefined
                  updated.shadow_angle = undefined; updated.shadow_spread = undefined
                  updated.shadow_blend_mode = undefined
                }
                if (effect === 'none' || effect === 'shadow') {
                  updated.outline_color = undefined
                }
                onChange(updated)
              }}
            >{{ none: '无效果', outline: '描边', shadow: '投影' }[effect]}</button>
          ))}
        </div>

        {meta.text_effect === 'outline' && (
          <div className="mt-3 pl-3 border-l-2 border-blue-200">
            <label className="block text-[10px] text-gray-500 mb-0.5">描边颜色</label>
            <ColorInput placeholder="#FFFFFF" value={meta.outline_color} onChange={v => updateField('outline_color', v)} />
          </div>
        )}

        {meta.text_effect === 'shadow' && (
          <div className="mt-3 pl-3 border-l-2 border-blue-200 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] text-gray-500 mb-0.5">投影颜色</label>
                <ColorInput placeholder="#444444" value={meta.shadow_color} onChange={v => updateField('shadow_color', v)} />
              </div>
              <div>
                <label className="block text-[10px] text-gray-500 mb-0.5">大小 (px)</label>
                <Input type="number" step="1" value={meta.shadow_blur ?? ''} onChange={e => updateField('shadow_blur', e.target.value ? parseInt(e.target.value) : undefined)} />
              </div>
              <div>
                <label className="block text-[10px] text-gray-500 mb-0.5">不透明度 (%)</label>
                <Input type="number" step="5" min="0" max="100" value={meta.shadow_opacity ?? ''} onChange={e => updateField('shadow_opacity', e.target.value ? parseInt(e.target.value) : undefined)} />
              </div>
              <div>
                <label className="block text-[10px] text-gray-500 mb-0.5">距离 (px)</label>
                <Input type="number" step="1" min="0" value={meta.shadow_distance ?? ''} onChange={e => updateField('shadow_distance', e.target.value ? parseInt(e.target.value) : undefined)} />
              </div>
              <div>
                <label className="block text-[10px] text-gray-500 mb-0.5">角度 (°)</label>
                <Input type="number" step="1" min="0" max="360" value={meta.shadow_angle ?? ''} onChange={e => updateField('shadow_angle', e.target.value ? parseInt(e.target.value) : undefined)} disabled={!meta.shadow_distance} />
              </div>
              <div>
                <label className="block text-[10px] text-gray-500 mb-0.5">扩展 (%)</label>
                <Input type="number" step="5" min="0" max="100" value={meta.shadow_spread ?? ''} onChange={e => updateField('shadow_spread', e.target.value ? parseInt(e.target.value) : undefined)} />
              </div>
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-1">混合模式</label>
              <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
                {(['normal', 'multiply'] as const).map(mode => (
                  <button key={mode} type="button"
                    className={clsx('px-2.5 py-0.5 text-xs transition-colors border-r last:border-r-0 border-gray-300',
                      (meta.shadow_blend_mode ?? 'normal') === mode ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50')}
                    onClick={() => updateField('shadow_blend_mode', mode === 'normal' ? undefined : mode)}
                  >{{ normal: '正常', multiply: '正片叠底' }[mode]}</button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Transform fields */}
      <div className="p-4">
        <div className="text-xs font-semibold text-gray-800 mb-2">变换</div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[10px] text-gray-500 mb-0.5">旋转角度</label>
            <Input type="number" step="1" placeholder="0" value={meta.rotation_angle ?? ''} onChange={e => updateField('rotation_angle', e.target.value ? parseFloat(e.target.value) : undefined)} />
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-0.5">字体缩放 X</label>
            <Input type="number" step="0.1" placeholder="1.0" value={meta.font_scale_x ?? ''} onChange={e => updateField('font_scale_x', e.target.value ? parseFloat(e.target.value) : undefined)} />
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-0.5">行间距</label>
            <Input type="number" step="0.1" placeholder="1.3" value={meta.line_spacing ?? ''} onChange={e => updateField('line_spacing', e.target.value ? parseFloat(e.target.value) : undefined)} />
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-0.5">文字对齐</label>
            <Select value={meta.text_align ?? ''} onChange={e => updateField('text_align', (e.target.value || undefined) as 'left' | 'center' | 'right' | undefined)}
              options={[{ value: '', label: '居中' }, { value: 'left', label: '左对齐' }, { value: 'right', label: '右对齐' }]} />
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// TextAreaEditor — Main Export
// ============================================================================

export function TextAreaEditor({
  packId,
  metadata,
  onChange,
  backgroundItemId,
  items,
}: {
  packId: string
  metadata: ItemRenderMetadata
  onChange: (metadata: ItemRenderMetadata) => void
  backgroundItemId?: string
  items?: ContentPackItem[]
}) {
  const [mirrorMode, setMirrorMode] = useState(false)
  const [localBox, setLocalBox] = useState<TextBox>(
    metadata.text_box ?? { top: 0, left: 0, width: 0, height: 0 }
  )

  const prevTextBoxRef = useRef(JSON.stringify(metadata.text_box))
  const metaRef = useRef(metadata)
  metaRef.current = metadata

  useEffect(() => {
    const key = JSON.stringify(metadata.text_box)
    if (key !== prevTextBoxRef.current) {
      prevTextBoxRef.current = key
      setLocalBox(metadata.text_box ?? { top: 0, left: 0, width: 0, height: 0 })
    }
  }, [metadata.text_box])

  const handleBoxChange = useCallback((box: TextBox) => {
    setLocalBox(box)
  }, [])

  const handleBoxCommit = useCallback((box: TextBox) => {
    setLocalBox(box)
    onChange({ ...metaRef.current, text_box: box })
  }, [onChange])

  const handleMetaChange = useCallback((updated: ItemRenderMetadata) => {
    if (updated.text_box) {
      setLocalBox(updated.text_box)
    }
    onChange(updated)
  }, [onChange])

  return (
    <div className="flex rounded-lg border border-gray-200 overflow-hidden" style={{ minHeight: 400 }}>
      <PreviewPanel
        packId={packId}
        itemId={backgroundItemId}
        meta={metadata}
        items={items}
        localBox={localBox}
        mirrorMode={mirrorMode}
        onMirrorToggle={() => setMirrorMode(m => !m)}
        onBoxChange={handleBoxChange}
        onBoxCommit={handleBoxCommit}
      />
      <FormPanel
        meta={metadata}
        onChange={handleMetaChange}
      />
    </div>
  )
}
